/**
 * useQuoteAutoSave — Continuous auto-save with debounce, retry, and Toast on leave.
 *
 * Implements rules:
 *   Q-2: Auto-save continuous; "Quote saved" snackbar on leave (no persistent indicator)
 *   Q-1: No "Draft" — quote becomes Active once ≥1 line item
 *
 * Behavior:
 * - Debounces saves (1.5s after last change)
 * - Retries failed saves (3 attempts with exponential backoff)
 * - Shows "Quote saved" Toast only when navigating away (not on every save)
 * - NO persistent "saving" indicator (Q-2 negative space)
 * - Queues changes while offline
 *
 * DEF-160 (2026-08-05): accepts optional `apiUpdate`/`apiCreate`/`dirtyCheckKeys`
 * overrides so the Change Order builder can consume the SAME hook (SD-1 ruling).
 * Defaults remain quote-specific for backward compatibility.
 *
 * Usage:
 *   const { isDirty, lastSaved, save, error } = useQuoteAutoSave(quoteId, quoteData);
 *   // CO builder:
 *   useQuoteAutoSave(coId, coPayload, { apiUpdate: (id, d) => api.updateChangeOrder(id, d), apiCreate: null, dirtyCheckKeys: CO_KEYS });
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { quotesAPI } from './quoteAPI';
import { parseApiError, isDocumentLocked } from '../utils/parseApiError';
import { ensureAliasesAgree } from './ensureAliasesAgree';
import { sendUnloadSave } from './unloadSave';

// IDI-425: a copy deep enough for ensureAliasesAgree, which writes to the
// payload's top level and to each line item. Anything deeper is passed by
// reference, unchanged.
function clonePayload(p) {
  if (!p || typeof p !== 'object') return p;
  const out = { ...p };
  for (const k of ['line_items', 'lineItems']) {
    if (Array.isArray(p[k])) out[k] = p[k].map((li) => (li && typeof li === 'object' ? { ...li } : li));
  }
  return out;
}

const DEBOUNCE_MS = 1500;
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 1000;

/**
 * @param {string|null} quoteId - Quote ID (null for new unsaved quotes)
 * @param {object} quoteData - Current quote state to persist
 * @param {object} options
 * @param {function} options.onSaved - Callback after successful save (receives { id, updatedAt, status })
 * @param {object} options.toast - Canonical toast API from `useToast()` — e.g. `toast.success('Quote saved')`.
 *                                 Optional; falsy value disables toasts. Previously named `showToast`
 *                                 and expected an object-shape argument; both were incompatible with
 *                                 the actual `useToast()` return — see PR-02e.
 * @param {boolean} options.enabled - Whether auto-save is active (default true)
 * @param {boolean} options.hydrated - IDI-347: whether the entity's async load has
 *   finished. While false, the dirty baseline is continuously re-seeded from the
 *   incoming data so async HYDRATION ticks (line_items/tax/margin arriving after
 *   mount) are never mistaken for user edits and never schedule an unrequested
 *   save. Defaults true (net-new quotes have nothing to load and save immediately).
 * @returns {object} Auto-save state and controls
 */
export default function useQuoteAutoSave(quoteId, quoteData, {
  onSaved,
  toast,
  enabled = true,
  // IDI-347: the builder loader flips this true once the quote's async load
  // completes. While false, autosave treats every render as "still hydrating"
  // and re-seeds its dirty baseline instead of diffing — so loading a quote does
  // NOT write to it (or, via onSaved's customer-writeback, to its customer).
  // Net-new quotes pass nothing and default to hydrated=true.
  hydrated = true,
  // PT-53A W3.1 (DEF-119): when true, strip price-bearing fields from the
  // update payload. Used when the quote is acknowledged (PT-42 lock) and
  // only non-price fields (customer info, notes, tags) should be saved.
  // Without this, editing customer info on an acknowledged quote triggers
  // a 409 because the full payload includes line_items/subtotal/total_amount.
  stripPriceFields = false,
  // DEF-160: API method overrides for non-quote entities (e.g. Change Orders).
  // Defaults to quotesAPI methods for backward compatibility.
  apiUpdate = null,
  // DEF-160 caller contract (regression-fixed 2026-08-08):
  //   apiCreate === undefined  → use quotesAPI.createQuote (DEFAULT).
  //   apiCreate === null       → explicit opt-out (CO builder pattern, no create).
  //   apiCreate === function   → use that function.
  //
  // Previously defaulted to `null` — identical to explicit opt-out — which meant
  // every caller that omitted the option got the "no create" branch and net-new
  // quotes silently skipped their POST. Regressed by DEF-160 (2026-08-05); found
  // 2026-08-08 P0 six days before launch. See useQuoteAutoSave.FL-02.test.js.
  apiCreate = undefined,
  // DEF-160: custom dirty-check keys. When provided, replaces the default
  // quote-specific DIRTY_CHECK_KEYS list.
  dirtyCheckKeys = null,
  // DEF-160: whether to run ensureAliasesAgree on the payload. CO builder
  // doesn't need alias reconciliation (its fields are already canonical).
  useAliases = true,
  // DEF-160: callback to transform data before save (e.g. assembleChangeOrderPayload).
  // When provided, the hook calls transformPayload(data, id) and saves the result.
  transformPayload = null,
  // IDI-336: API path for the document-unload keepalive save. Defaults to
  // the quote route; the CO builder passes id => `/v1/change-orders/${id}`.
  unloadPath = null,
} = {}) {
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  // PT-43.1 (2026-07-28): PT-42 rejects price mutations on acknowledged quotes
  // with a 409 `{ error: "document_locked" }`. Retrying makes no sense (the
  // block is permanent, not transient), so we surface it structurally and let
  // the consumer route to the CO exit affordance.
  const [lockedError, setLockedError] = useState(null);

  // Refs to avoid stale closures
  const quoteDataRef = useRef(quoteData);
  const quoteIdRef = useRef(quoteId);
  const debounceTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const lastSavedDataRef = useRef(null);
  // IDI-313 (2026-09-10): tracks `isDirty` for the unmount-flush effect below
  // without a stale closure (isDirty itself is plain state, not a ref).
  const isDirtyRef = useRef(false);
  // IDI-313 (2026-09-10): tracks the latest `performSave` identity so the
  // TRUE-unmount-only flush effect (empty dep array, below) can call the
  // current version without re-running its cleanup on every render where
  // performSave's useCallback deps change (which happens more often than
  // true unmounts — the DEF-160 double-save regression this ref prevents).
  const performSaveRef = useRef(null);
  // IDI-336: set once the unload keepalive save has fired, so beforeunload +
  // pagehide don't both PUT. Re-armed on the next edit.
  const unloadFlushedRef = useRef(false);
  // DEF-160: separate ref for "last scheduled" to prevent the DEF-155 loop
  // without conflating it with "last saved" (which gates the debounce callback).
  const lastScheduledPickRef = useRef(null);
  // IDI-425: the last `status` the payload carried when a save was scheduled
  // (default quote keys only). See the dirty-check effect.
  const lastScheduledStatusRef = useRef(undefined);
  // IDI-435: the in-flight CREATE of a net-new quote (null when none). The id
  // only reaches quoteIdRef when the create resolves (~1.4 s), so without this
  // a debounce tick, saveNow or unmount flush in that window POSTed a second
  // create (Q-0123 empty draft beside Q-0124). Saves arriving meanwhile wait
  // for it and then UPDATE the created quote.
  const createInFlightRef = useRef(null);

  // Update refs when props change
  useEffect(() => {
    quoteDataRef.current = quoteData;
    quoteIdRef.current = quoteId;
  }, [quoteData, quoteId]);

  // IDI-313 (2026-09-10): keep isDirtyRef in sync so the flush-on-unmount
  // effect below can read the latest dirty state without a stale closure.
  useEffect(() => {
    isDirtyRef.current = isDirty;
    if (isDirty) unloadFlushedRef.current = false;
  }, [isDirty]);

  // Track mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // IDI-336: the outbound-payload preparation shared by performSave and the
  // document-unload save, so both send byte-identical payloads.
  const preparePayload = useCallback((data, id) => {
    let saveData = data;
    if (id && stripPriceFields) {
      // IDI-398 (ruled 2026-10-05 / D-01): a SIGNED quote may change only its
      // internal notes, tags and internal files. Send just those, so the
      // server's allow-list (cmd/quotes signed_lock.go) never sees a locked
      // field and autosave can't 409. IDI-305: internal notes left autosave
      // (they go through ?action=add_note), so only tags remain here.
      const SIGNED_EDITABLE = ['tags'];
      saveData = Object.fromEntries(
        Object.entries(data).filter(([k]) => SIGNED_EDITABLE.includes(k)),
      );
    }
    // PT-53A DEF-149 (2026-08-03): write-time alias PREVENTION.
    // Reconciles known alias pairs on the outbound payload so we do not
    // emit new divergent rows (lineTotal:null with line_total:$ shape,
    // etc.). This is a write-side prevention hook — NOT a migration.
    // Existing rows are repaired by a separate CTO-owned migration
    // (DATA_SEEDING_CONTRACT R-3 requires a reconciliation triple that a
    // lazy write-through cannot emit). See ensureAliasesAgree.js header.
    // Rule 3 (revised for launch safety per operator ruling 2026-08-03
    // section 1c): both sides present and different -- record via
    // onConflict, do NOT throw. Throwing would break saves on the 40
    // divergent-customerId rows already in the tenant. onConflict emits
    // a structured telemetry record ([DEF-149] prefix, greppable in
    // DevTools console and any log-aggregation client-side) that feeds
    // DEF-121 routing; alias-divergence-probe.js can also intercept it
    // in dev sessions.
    const onAliasConflict = (record) => {
      try {
        // eslint-disable-next-line no-console
        console.warn('[DEF-149] alias-conflict', record);
      } catch (_e) { /* console unavailable -- swallow */ }
    };
    // DEF-160: apply alias reconciliation only when useAliases is true
    // (quote builder). CO builder skips this — its fields are canonical.
    let finalSaveData = saveData;
    let finalCreateData = id ? null : data;
    if (useAliases) {
      // IDI-425: reconcile on a COPY. ensureAliasesAgree mutates in place (it
      // fills `customerName` from `customer_name`, and each line's camel/snake
      // twins). `data` here is the caller's memoised payload object, so the
      // mutation leaked into React state: after every save the memo held
      // keys it never produced, the next recompute (without them) read as a
      // change to `customerName`/`line_items`, and autosave fired a second,
      // byte-identical PUT. Measured 2026-10-15: one line edit = 2 identical
      // PUT /v1/quotes, plus 2 more after the first create.
      finalSaveData = ensureAliasesAgree(clonePayload(saveData), { onConflict: onAliasConflict });
      finalCreateData = id ? null : ensureAliasesAgree(clonePayload(data), { onConflict: onAliasConflict });
    }
    // DEF-160: apply transformPayload if provided (e.g. assembleChangeOrderPayload).
    if (transformPayload) {
      finalSaveData = transformPayload(finalSaveData, id);
      if (finalCreateData) finalCreateData = transformPayload(finalCreateData, null);
    }
    return { finalSaveData, finalCreateData };
  }, [stripPriceFields, useAliases, transformPayload]);

  /**
   * Perform the actual save operation with retry logic.
   */
  const performSave = useCallback(async (data, id, opts = {}) => {
    // IDI-313 (2026-09-10): `opts.bypassMountGuard` lets the flush-on-unmount
    // effect fire the network write even though isMountedRef.current is
    // already false by the time its cleanup runs (the mount-tracking
    // effect's cleanup runs first — React walks effect cleanups in FORWARD
    // declaration order, not reverse; verified empirically by
    // useQuoteAutoSave.idi313-unmount-flush.test.js, which failed before
    // this fix). The state-update guards LATER in this function (after the
    // network call resolves) are intentionally left untouched — updating
    // state on an unmounted component is still wrong; only the ENTRY guard
    // is bypassable, because firing the write itself is the whole point of
    // a flush.
    if (!isMountedRef.current && !opts.bypassMountGuard) return;

    // IDI-435: a create is already in flight -> wait for it, then save the
    // LATEST data as an update to the quote it created (never a 2nd create).
    if (!id && createInFlightRef.current) {
      const createdId = await createInFlightRef.current;
      if (createdId) {
        return performSave(quoteDataRef.current, createdId, opts);
      }
      // The create failed: fall through and let this save try again.
    }

    setIsSaving(true);
    setError(null);

    try {
      let result;
      // PT-53A W3.1 (DEF-119): strip price-bearing fields when the quote is
      // acknowledged. The backend's PT-42 lock rejects any payload containing
      // these fields with 409. Rather than sending them and catching the error,
      // proactively exclude them so customer-info-only edits can save.
      const { finalSaveData, finalCreateData } = preparePayload(data, id);
      // DEF-160: use configurable API methods (defaults to quotesAPI).
      const updateFn = apiUpdate || quotesAPI.updateQuote.bind(quotesAPI);
      const createFn = apiCreate === null ? null : (apiCreate || quotesAPI.createQuote.bind(quotesAPI));
      if (id) {
        result = await updateFn(id, finalSaveData);
      } else if (createFn) {
        const createPromise = Promise.resolve(createFn(finalCreateData));
        // Never rejects (a failed create resolves null) so no waiter leaks an
        // unhandled rejection; this call's own catch below still sees the error.
        createInFlightRef.current = createPromise.then((r) => r?.id || null, () => null);
        try {
          result = await createPromise;
        } finally {
          createInFlightRef.current = null;
        }
      } else {
        // No create function (e.g. CO builder — entity is always pre-created).
        // Skip save silently when id is missing.
        setIsSaving(false);
        return;
      }

      if (!isMountedRef.current) return;

      setIsSaving(false);
      setIsDirty(false);
      // IDI-336: only clear the ref if nothing new was edited while this save
      // was in flight (quoteDataRef moved on => still dirty for unload).
      if (quoteDataRef.current === data) isDirtyRef.current = false;
      setLastSaved(new Date());
      lastSavedDataRef.current = data;
      retryCountRef.current = 0;

      if (onSaved) {
        onSaved({
          id: result?.id || id,
          updatedAt: result?.updated_at || new Date().toISOString(),
          // IDI-313: thread the server-side status back to the caller. For a
          // NET-NEW quote the create response carries status: 'draft'; the
          // caller (QuoteBuilderPage) seeds quoteMeta.status from it so
          // shouldActivateOnSave(status, count) can ever be true. Without
          // this, quoteMeta.status stays null (the LOAD effect only fires for
          // existing quotes) and the quote is stuck in draft forever.
          status: result?.status,
          // IDI-345: thread the server-assigned quote number back so the caller
          // (QuoteBuilderPage) can render "Quote Q-00NN" in the header
          // immediately after create instead of the raw UUID (formatQuoteNumber
          // falls back to id when metadata.quoteNumber is absent) until reload.
          quoteNumber: result?.quoteNumber || result?.quote_number,
        });
      }

      // PT-53A W4 (DEF-135): update the ref IMMEDIATELY after a successful
      // create, not just via the state→useEffect path. Without this, there's
      // a race: setQuoteId(savedId) triggers a re-render, but quoteIdRef
      // syncs via useEffect (which runs AFTER the render). If onLeave is
      // called before the next render completes, it reads stale null from
      // the ref and the guard fires an error toast even though the save
      // succeeded. This is the DEF-135 dual-toast root cause.
      const resolvedId = result?.id || id || null;
      if (resolvedId && !quoteIdRef.current) {
        quoteIdRef.current = resolvedId;
      }

      // PT-14 F (Wave 1): return the saved id so callers (handleReviewSend)
      // can navigate synchronously on the first save of a brand-new quote.
      return resolvedId;
    } catch (err) {
      if (!isMountedRef.current) return;

      // PT-43.1: 409 document_locked is a permanent block, not a transient
      // network failure. Skip the retry loop, clear state, and surface the
      // parsed error so the QuoteBuilder can offer the CO exit.
      const parsed = parseApiError(err);
      if (isDocumentLocked(parsed)) {
        setIsSaving(false);
        setLockedError(parsed);
        setError(parsed.reason || 'This quote has been accepted and its price is frozen.');
        retryCountRef.current = 0;
        return;
      }

      retryCountRef.current += 1;

      if (retryCountRef.current < MAX_RETRIES) {
        // Exponential backoff retry
        const delay = RETRY_BASE_MS * Math.pow(2, retryCountRef.current - 1);
        setTimeout(() => {
          if (isMountedRef.current) {
            performSave(quoteDataRef.current, quoteIdRef.current);
          }
        }, delay);
      } else {
        // Max retries exceeded
        setIsSaving(false);
        setError(`Auto-save failed: ${err.message || 'Network error'}`);
        retryCountRef.current = 0;
      }
    }
  }, [onSaved, preparePayload, apiUpdate, apiCreate]);

  // IDI-313 (2026-09-10): keep performSaveRef in sync so the true-unmount
  // flush effect below (empty dep array) always calls the CURRENT
  // performSave without needing performSave in its own dependency array
  // (which would re-fire the cleanup on every performSave identity change,
  // not just true unmounts — see the DEF-160 double-save regression this
  // split fixes).
  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  // IDI-313 (2026-09-10): flush a pending save on ROUTE CHANGE, not only
  // browser tab close (beforeunload).
  //
  // CORRECTION (2026-09-10, review): the original version of this comment
  // claimed React cleans up effects in REVERSE declaration order, so this
  // effect's cleanup (declared after mount-tracking) would run FIRST on
  // unmount, before isMountedRef.current flips false. That claim was
  // WRONG — React walks effect cleanups in FORWARD declaration order.
  // The mount-tracking effect's cleanup (isMountedRef.current = false)
  // runs BEFORE this effect's cleanup regardless of where this effect is
  // declared. Verified empirically by
  // useQuoteAutoSave.idi313-unmount-flush.test.js, which FAILED (0 saves,
  // not 1) before the fix below.
  //
  // The actual fix: performSave accepts `{ bypassMountGuard: true }` to
  // skip ONLY its entry guard. The state-update guards deeper in
  // performSave (after the network call resolves) are untouched —
  // updating React state on an unmounted component is still wrong; firing
  // the network write itself is the whole point of a flush.
  //
  // Previously: navigating away in-app (sidebar click, back button, tab
  // switch to another quote) unmounted the builder while a debounced save
  // was still pending. The debounce-clear effect (quoteId dep, below) silently
  // discarded the timer with no flush — the last few keystrokes were lost
  // with no error, no toast, nothing. Only a full page unload (beforeunload)
  // was covered before this fix.
  //
  // EMPTY dep array — this effect mounts/unmounts exactly once per component
  // lifetime. Its cleanup fires ONLY on true unmount, not on every render
  // where some other dependency's identity changes (that's what the
  // performSaveRef indirection above is for).
  useEffect(() => {
    return () => {
      if (isDirtyRef.current && debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        // Fire-and-forget: the component is unmounting, so there's no state
        // to update on completion. Same shape as the beforeunload handler's
        // flush (which also doesn't await). bypassMountGuard:true is
        // required — see the CORRECTION above.
        performSaveRef.current?.(quoteDataRef.current, quoteIdRef.current, { bypassMountGuard: true });
      }
    };
  }, []);

  /**
   * Schedule a debounced save.
   */
  const scheduleSave = useCallback(() => {
    if (!enabled) return;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      const data = quoteDataRef.current;
      const id = quoteIdRef.current;

      performSave(data, id);
    }, DEBOUNCE_MS);
  }, [enabled, performSave]);

  /**
   * Mark as dirty and schedule save whenever quoteData changes.
   */
  useEffect(() => {
    if (!enabled) return;
    if (!quoteData) return;

    // DEF-155 (2026-08-04): allow-list dirty-check stops save loop.
    // Server fields can't trigger dirty. Key-order-insensitive comparison
    // prevents the address-reorder feedback loop (server returns keys in
    // alphabetical order; client sends in declaration order; values are
    // identical but JSON.stringify differs).
    // DEF-160: use configurable dirtyCheckKeys when provided.
    const ACTIVE_DIRTY_KEYS = dirtyCheckKeys || [
      'customerId', 'customer_id', 'customerName', 'customer_name',
      'line_items', 'discounts', 'notes',
      'validity_days', 'title', 'quote_options',
      'payment_term', 'payment_type', 'billing_address', 'service_address',
      'same_as_billing', 'lead_source', 'salespersons', 'salesperson_ids',
      'overall_margin', 'total_amount', 'tax_rate', 'tax_source',
      'subtotal', 'subtotal_snapshot',
    ];
    // IDI-425: activation. The quote builder adds `status: 'active'` to the
    // payload once a draft has lines (shouldActivateOnSave), AFTER the create
    // returns, and drops it again once the quote is active. That flip must
    // schedule a save or a new quote stays a draft (it used to ride along on
    // the duplicate PUT this issue removed). Only a status that is PRESENT and
    // NEW counts, so the key going away afterwards writes nothing. Callers
    // with their own dirtyCheckKeys (the CO builder lists 'status') keep their
    // own rule.
    const statusNow = dirtyCheckKeys ? undefined : quoteData.status;
    const statusChanged = statusNow != null && statusNow !== lastScheduledStatusRef.current;
    const noteStatus = () => {
      if (statusNow != null) lastScheduledStatusRef.current = statusNow;
    };
    const stableStringify = (val) => {
      if (val === null || val === undefined) return JSON.stringify(val);
      if (Array.isArray(val)) return '[' + val.map(stableStringify).join(',') + ']';
      if (typeof val === 'object') {
        const keys = Object.keys(val).sort();
        return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(val[k])).join(',') + '}';
      }
      return JSON.stringify(val);
    };
    const pick = (obj) => {
      if (!obj || typeof obj !== 'object') return '';
      const out = {};
      for (const k of ACTIVE_DIRTY_KEYS) {
        if (k in obj) out[k] = obj[k];
      }
      return stableStringify(out);
    };

    // Skip the initial render (no data to compare against).
    // Initialize the ref so subsequent changes can be detected.
    if (lastSavedDataRef.current === null) {
      lastSavedDataRef.current = quoteData;
      lastScheduledPickRef.current = pick(quoteData);
      noteStatus();
      return;
    }

    // IDI-347: while the entity is still HYDRATING (async load not finished),
    // re-seed the baseline every render instead of diffing. An existing quote
    // mounts empty (total 0, no line_items/tax/margin) and the loader fills
    // those ACTIVE_DIRTY_KEYS a tick later — without this gate each hydration
    // step reads as a user edit and schedules an unrequested save (which then
    // fires onSaved's customer-writeback, duplicating contacts — IDI-341).
    // Keeping the baseline pinned to the incoming data until hydrated=true means
    // the just-loaded payload becomes the baseline, so opening a quote writes
    // nothing. Net-new quotes default hydrated=true and are unaffected.
    if (!hydrated) {
      lastSavedDataRef.current = quoteData;
      lastScheduledPickRef.current = pick(quoteData);
      noteStatus();
      return;
    }

    if (pick(quoteData) === lastScheduledPickRef.current && !statusChanged) {
      return;
    }

    setIsDirty(true);
    // IDI-336: mark dirty on the REF synchronously too. The isDirty ->
    // isDirtyRef sync effect runs a render later; an unload in that gap would
    // read the ref as clean and drop the edit.
    isDirtyRef.current = true;
    unloadFlushedRef.current = false;
    scheduleSave();

    // DEF-155: update the SCHEDULED ref (not the saved ref) to prevent the
    // memo-recompute loop. The dirty-check compares against what was last
    // scheduled; the debounce callback compares against what was last SAVED.
    lastScheduledPickRef.current = pick(quoteData);
    noteStatus();
  }, [quoteData, enabled, scheduleSave, quoteId, dirtyCheckKeys, hydrated]);

  // DEF-160 §3: clear the debounce timer when the entity ID changes.
  // This prevents a timer scheduled for entity-A from firing after the
  // builder has switched to entity-B (cross-record write risk).
  // On unmount, isMountedRef.current=false gates performSave anyway.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [quoteId]);

  /**
   * Q-2: Show "Quote saved" Toast when navigating away (beforeunload + route change).
   */
  useEffect(() => {
    // IDI-336 (ARM 2): a full document unload (tab close, Back, address bar,
    // external link) with an edit still inside the debounce. performSave
    // can't be used here: its axios interceptor awaits fetchAuthSession()
    // and the page is gone before the PUT is sent (measured: ZERO requests).
    // sendUnloadSave is synchronous and uses fetch keepalive, which the
    // browser completes after the document is destroyed.
    //
    // Runs from BOTH beforeunload and pagehide: mobile Safari and bfcache
    // navigations can skip beforeunload. unloadFlushedRef stops a double PUT
    // when both fire; it re-arms on the next edit.
    const flushForUnload = () => {
      if (!isDirtyRef.current || unloadFlushedRef.current) return;
      const id = quoteIdRef.current;
      const data = quoteDataRef.current;
      if (!id || !data) return; // net-new, never-created quote: nothing to PUT
      unloadFlushedRef.current = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const { finalSaveData } = preparePayload(data, id);
      const path = unloadPath ? unloadPath(id) : `/v1/quotes/${id}`;
      const outcome = sendUnloadSave(path, finalSaveData);
      if (outcome !== 'sent') {
        // eslint-disable-next-line no-console
        console.warn('[IDI-336] unload save not sent:', outcome);
      }
    };
    // IDI-336 (RULED, Adrian 2026-09-29): NO "Leave site?" prompt. The edit
    // is saved on the way out, so the browser's "changes may not be saved"
    // warning was false and fired on every quick exit. The handler only
    // flushes: it must never call preventDefault() or set returnValue, either
    // of which brings the prompt back.
    //
    // Accepted by the same ruling: in the two cases keepalive can't carry the
    // save (no session; payload > 64 KB) flushForUnload logs a warning and the
    // page still closes. The last debounce window of edits is lost there.
    window.addEventListener('beforeunload', flushForUnload);
    window.addEventListener('pagehide', flushForUnload);
    return () => {
      window.removeEventListener('beforeunload', flushForUnload);
      window.removeEventListener('pagehide', flushForUnload);
    };
  }, [preparePayload, unloadPath]);

  /**
   * Force an immediate save (bypasses debounce).
   * Used when user explicitly triggers save or navigates away.
   *
   * PT-14 F (Wave 1, 2026-07-24): returns the saved id so callers can
   * navigate cleanly after the first save of a brand-new quote.
   */
  const saveNow = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const data = quoteDataRef.current;
    const id = quoteIdRef.current;

    const savedId = await performSave(data, id);

    // Q-2: Show "Quote saved" snackbar on explicit save/leave.
    // PT-53A W4+ (DEF-135 sibling): only toast success when the save
    // actually returned a resolved id. performSave returns undefined from
    // its catch block on failure (retry-scheduled, max-retries, or
    // document-locked); toasting unconditionally produces the dual
    // "saved" + "couldn't save" toast the operator sees when create
    // fails. The correct behavior: no success toast on failure, so the
    // caller's guard (computeReviewSendTarget) produces the sole error
    // toast. Guarded by FL-02 RP-DUAL-{1..5}.
    if (toast && savedId) {
      toast.success('Quote saved');
    }

    return savedId || null;
  }, [performSave, toast]);

  /**
   * Call this when the user navigates away from the builder.
   * Flushes pending changes and shows the Toast.
   *
   * PT-14 F (Wave 1): returns the saved id (or existing id) so the caller
   * has something to navigate to even on a brand-new quote's first save.
   */
  const onLeave = useCallback(async () => {
    if (isDirty) {
      return await saveNow();
    }
    // PT-53A W4 (DEF-135): removed the "Quote saved" toast from the
    // non-dirty branch. This toast fired when the debounced auto-save had
    // already completed (isDirty=false, lastSaved=truthy) and onLeave was
    // called immediately after. Combined with the guard's error toast
    // (when quoteIdRef was stale null), it produced the dual-toast:
    // "saved" + "couldn't save" simultaneously. The save already toasted
    // on its own completion; re-toasting here is redundant.
    return quoteIdRef.current || null;
  }, [isDirty, saveNow]);

  /**
   * PT-14 A2 (Wave 1, 2026-07-24): cancel any pending debounced save.
   * Called by the builder loader effect when the URL id changes so a
   * debounced save from quote A can't fire against the (now-changing)
   * id ref and write A's data to B's row.
   */
  const cancelPending = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  return {
    // State (read-only)
    isDirty,
    isSaving,       // NOTE: Do NOT render a persistent indicator (Q-2)
    lastSaved,
    error,
    lockedError,    // PT-43.1: parsed document_locked payload, or null. Presence indicates the CO exit should be offered.

    // Actions
    saveNow,        // Force immediate save
    onLeave,        // Call when navigating away
    cancelPending,  // PT-14 A2: cancel pending debounce (used on id change)

    // For testing
    _retryCount: retryCountRef.current,
  };
}
